import { Menu } from "lucide-react";
import { useMemo, type ComponentType } from "react";

import { Breadcrumbs } from "./Breadcrumbs";
import { InboxBell } from "./InboxBell";
import { GlobalSearch } from "./search/GlobalSearch";
import { Badge } from "@/components/ui/badge";
import { AdminEntryButton } from "@/components/layout/AdminEntryButton";
import { LauncherEntryButton } from "@/components/layout/LauncherEntryButton";
import { detectEnvironment } from "@/lib/environment";
import { useFeature } from "@/lib/features";
import { formatCurrentDate } from "@/lib/format";
import { useAuthSession } from "@/lib/session-context";
import { cn } from "@/lib/utils";

interface TopbarProps {
  onOpenMobileNav: () => void;
  /** True when the sidebar isn't rendered at all (demo mode). Drops the
   *  mobile nav-toggle — there's no drawer to open, and demo mode is meant
   *  to have no way back to the sidebar. */
  sidebarHidden?: boolean;
  /** True when `Topbar` is rendered inside the Admin app. Drops only the
   *  Admin entry button — a "go to Admin" gear while already in Admin has
   *  nothing useful to do. The "back to apps" affordance still shows. */
  inAdminApp?: boolean;
  /** The active module's own top-bar control (`ModuleDefinition.topbarAction`),
   *  beside the bell — so it shows in that module and nowhere else. */
  moduleAction?: ComponentType;
}

export function Topbar({
  onOpenMobileNav,
  sidebarHidden = false,
  inAdminApp = false,
  moduleAction: ModuleAction,
}: TopbarProps) {
  const env = useMemo(() => detectEnvironment(), []);
  const date = useMemo(() => formatCurrentDate(), []);
  const notificationsEnabled = useFeature("notifications");
  const { demo_mode } = useAuthSession();

  return (
    <div
      className="sticky top-viewing-as z-30 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background-deep px-4 md:px-6"
      style={{ transition: "background var(--duration-theme), border-color var(--duration-theme)" }}
    >
      {!sidebarHidden && (
        <button
          type="button"
          onClick={onOpenMobileNav}
          aria-label="Open navigation"
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-row-hover hover:text-foreground lg:hidden"
        >
          <Menu size={16} />
        </button>
      )}

      <Breadcrumbs className="min-w-0 flex-1 sm:flex-initial" />

      <div className="ml-auto flex items-center gap-3 md:gap-4">
        {/* Global search — hidden below md, reclaimed by environment + theme. */}
        <GlobalSearch />

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {/* Demo Mode session indicator — shows only while active. Marks
              fabricated sample data so advisors can't mistake a demo session
              for production; toggle it from the Energy Advisor landing page or
              the demo banner's Exit button. */}
          {demo_mode && <Badge variant="warning">Demo</Badge>}
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
              env.toneClass,
            )}
          >
            <span
              className={cn("inline-block h-[5px] w-[5px] rounded-full", env.dotClass)}
              aria-hidden="true"
            />
            {env.label}
          </span>
          {/* Date hidden on phones to keep the header from wrapping. */}
          <span className="hidden tabular-nums text-text-dim sm:inline">{date}</span>
        </div>

        {ModuleAction && !demo_mode && <ModuleAction />}
        {notificationsEnabled && <InboxBell />}

        {/* Launcher + Admin entry points. Hidden in demo mode — demo
            tenants are confined to Sales, so
            both would be dead ends (same gate as the sidebar being hidden for
            them). The Admin gear additionally hides inside Admin itself —
            see `inAdminApp` doc. */}
        {!demo_mode && (
          <>
            <LauncherEntryButton />
            {!inAdminApp && <AdminEntryButton />}
          </>
        )}
      </div>
    </div>
  );
}
